<template>
    <div class="preview-group-controls">
        <template v-if="scaling">
            <p>Ad scaling:</p>

            <Select
                data-testid="preview-group-controls-ad-scaling"
                :model-value="scaling.scalingFactor"
                @onChange="emit('setScalingFactor', $event)"
            >
                <Option data-testid="preview-group-controls-ad-scaling-option-auto" :value="0">Auto</Option>
                <Option data-testid="preview-group-controls-ad-scaling-option-25" :value="0.25">25%</Option>
                <Option data-testid="preview-group-controls-ad-scaling-option-50" :value="0.5">50%</Option>
                <Option data-testid="preview-group-controls-ad-scaling-option-75" :value="0.75">75%</Option>
                <Option data-testid="preview-group-controls-ad-scaling-option-100" :value="1">100%</Option>
            </Select>
        </template>

        <div class="preview-group-controls__scrubbar">
            <BannerScrubbar
                v-if="scrubbar"
                :model-value="scrubbar.currentTime"
                :duration="scrubbar.duration"
                @update="emit('setBannerPlaybackTime', $event)"
            />

            <slot name="annotations" />
        </div>

        <SquareButton v-if="replayButton" has-inverse-hover @click="emit('replay')">
            <Icon type="md-refresh" />
        </SquareButton>

        <SquareButton v-if="playPauseButton" has-inverse-hover @click="emit('playPause')">
            <Icon :type="playPauseButton.isPlaying ? 'md-pause' : 'md-play'" />
        </SquareButton>

        <Page
            v-if="pagination"
            class="preview-group-controls__page-selector"
            :model-value="pagination.currentPage"
            :page-size="pagination.pageSize"
            :total="pagination.total"
            @onChange="emit('setPage', $event)"
        />

        <Select
            v-if="pageSizeControls"
            :model-value="pageSizeControls.pageSize"
            @onChange="emit('setPageSize', $event)"
        >
            <Option v-for="option in pageSizeControls.options" :key="option.value" :value="option.value">
                {{ option.label }}
            </Option>
        </Select>
    </div>
</template>

<script lang="ts" setup>
import { type Ref } from "vue";

import BannerScrubbar from "@/components/Previews/BannerScrubbar.vue";
import SquareButton from "@/components/common/SquareButton.vue";

type Props = {
    scaling?: {
        scalingFactor: number;
    };

    scrubbar?: {
        currentTime: Ref<number>;
        duration: Ref<number>;
    };

    replayButton?: boolean;

    playPauseButton?: {
        isPlaying: boolean;
    };

    pagination?: {
        currentPage: number;
        pageSize: number;
        total: number;
    };

    pageSizeControls?: {
        pageSize: number;
        options: { value: number; label: string }[];
    };
};

const { scaling, scrubbar, replayButton, pagination, pageSizeControls } = defineProps<Props>();

const emit = defineEmits<{
    (e: "setScalingFactor", arg1: number): void;
    (e: "setBannerPlaybackTime", arg1: number): void;
    (e: "replay"): void;
    (e: "playPause"): void;
    (e: "setPage", arg1: number): void;
    (e: "setPageSize", arg1: number): void;
}>();
</script>

<style lang="scss" scoped>
.preview-group-controls {
    border-top: 1px solid #dcdcdc;
    background-color: #f4f5f7;

    width: 100%;
    height: 64px;

    padding: 0 24px;

    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 16px;

    .ivu-select {
        width: unset;
        background-color: #ffffff;
    }
}

.preview-group-controls__scrubbar {
    flex: auto;
    position: relative;
}

.preview-group-controls__page-selector {
    margin-left: auto;
}
</style>
